import { useState } from 'react';
import PageHeader from '../components/PageHeader';
import { API_BASE } from '../apiBase';

const colors = {
  bg: '#f0f1f3',
  cardBg: '#ffffff',
  navy: '#071a52',
  greenDark: '#5cb83c',
  textPrimary: '#1a1a2e',
  textSecondary: '#6b7280',
  border: '#e2e4e8',
};

const PROXY = API_BASE;

function QuickAdd() {
  const [partNo, setPartNo] = useState('');
  const [type, setType] = useState('PART');
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupError, setLookupError] = useState(null);
  const [looking, setLooking] = useState(false);

  const [colorId, setColorId] = useState('');
  const [colorName, setColorName] = useState('');
  const [qty, setQty] = useState('1');
  const [price, setPrice] = useState('');
  const [condition, setCondition] = useState('N');
  const [bin, setBin] = useState('-INCOMING');

  const [pushing, setPushing] = useState(false);
  const [pushResult, setPushResult] = useState(null);
  const [pushError, setPushError] = useState(null);

  async function handleLookup() {
    if (!partNo.trim()) return;
    setLooking(true);
    setLookupError(null);
    setLookupResult(null);
    setPushResult(null);
    setPushError(null);

    const pno = partNo.trim();
    const apiType = type === 'MINIFIG' ? 'minifig' : 'part';

    try {
      const res = await fetch(`${PROXY}/api/items/${apiType}/${pno}`);
      const json = await res.json();
      if (!json.data) throw new Error('Not found');
      setLookupResult(json.data);

      // Fetch price guide for suggested price
      try {
        const priceRes = await fetch(`${PROXY}/api/price/${apiType}/${pno}?color_id=${colorId || 0}&guide_type=sold`);
        const priceJson = await priceRes.json();
        const avg = priceJson.data?.avg_price;
        if (avg && !price) setPrice(parseFloat(avg).toFixed(3));
      } catch (e) { /* no price suggestion */ }
    } catch (err) {
      setLookupError(`Could not find ${type === 'MINIFIG' ? 'minifig' : 'part'} "${pno}". Check the number and try again.`);
    }
    setLooking(false);
  }

  async function handlePush() {
    if (!lookupResult || !qty || parseInt(qty) <= 0) return;
    setPushing(true);
    setPushError(null);
    setPushResult(null);

    try {
      const payload = {
        item: { no: partNo.trim(), type: type },
        color_id: parseInt(colorId) || 0,
        quantity: parseInt(qty),
        unit_price: parseFloat(price) || 0,
        new_or_used: condition,
        remarks: bin.trim().toUpperCase() || '-INCOMING',
        is_retain: true,
        is_stock_room: false,
      };

      const res = await fetch(`${PROXY}/api/inventories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText);
      }

      setPushResult(`Added ${qty}x ${partNo.trim()} to ${bin.trim().toUpperCase() || '-INCOMING'}`);
      // Reset for next entry
      setPartNo('');
      setLookupResult(null);
      setColorId('');
      setColorName('');
      setQty('1');
      setPrice('');
      setBin('-INCOMING');
    } catch (err) {
      setPushError(err.message);
    }
    setPushing(false);
  }

  function getImageUrl() {
    const pno = partNo.trim();
    if (type === 'MINIFIG') return `https://img.bricklink.com/ItemImage/MN/0/${pno}.png`;
    return `https://img.bricklink.com/ItemImage/PN/${colorId || 0}/${pno}.png`;
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: colors.bg,
      padding: '20px',
      fontFamily: "'Outfit', sans-serif",
    }}>
      <PageHeader title="Quick Add" icon="+" />

      {/* Success message */}
      {pushResult && (
        <div style={{
          background: '#f0fdf4',
          border: '1px solid #bbf7d0',
          borderLeft: `4px solid ${colors.greenDark}`,
          borderRadius: '6px',
          padding: '14px 16px',
          marginBottom: '16px',
          fontSize: '0.88rem',
          color: colors.greenDark,
          fontWeight: 700,
        }}>
          ✓ {pushResult}
        </div>
      )}

      {/* Type toggle */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '16px' }}>
        <button
          onClick={() => { setType('PART'); setLookupResult(null); setLookupError(null); }}
          style={{
            flex: 1,
            padding: '10px',
            background: type === 'PART' ? colors.navy : colors.cardBg,
            color: type === 'PART' ? '#fff' : colors.textPrimary,
            border: `1px solid ${type === 'PART' ? colors.navy : colors.border}`,
            borderRadius: '6px',
            fontFamily: "'Outfit', sans-serif",
            fontWeight: 700,
            fontSize: '0.85rem',
            cursor: 'pointer',
          }}
        >Part</button>
        <button
          onClick={() => { setType('MINIFIG'); setLookupResult(null); setLookupError(null); }}
          style={{
            flex: 1,
            padding: '10px',
            background: type === 'MINIFIG' ? '#d97706' : colors.cardBg,
            color: type === 'MINIFIG' ? '#fff' : colors.textPrimary,
            border: `1px solid ${type === 'MINIFIG' ? '#d97706' : colors.border}`,
            borderRadius: '6px',
            fontFamily: "'Outfit', sans-serif",
            fontWeight: 700,
            fontSize: '0.85rem',
            cursor: 'pointer',
          }}
        >Minifig</button>
      </div>

      {/* Search */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
        <input
          type="text"
          placeholder={type === 'MINIFIG' ? 'Minifig # (e.g. sw1308)' : 'Part # (e.g. 3001)'}
          value={partNo}
          onChange={e => setPartNo(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleLookup(); }}
          style={{
            flex: 1,
            padding: '14px 16px',
            background: colors.cardBg,
            border: `1px solid ${colors.border}`,
            borderRadius: '6px',
            color: colors.textPrimary,
            fontFamily: "'Outfit', sans-serif",
            fontSize: '0.95rem',
            outline: 'none',
          }}
        />
        <button
          onClick={handleLookup}
          disabled={looking || !partNo.trim()}
          style={{
            padding: '14px 24px',
            background: partNo.trim() && !looking ? colors.navy : '#e2e4e8',
            color: partNo.trim() && !looking ? '#fff' : colors.textSecondary,
            border: 'none',
            borderRadius: '6px',
            fontSize: '0.95rem',
            fontWeight: 800,
            fontFamily: "'Outfit', sans-serif",
            cursor: partNo.trim() && !looking ? 'pointer' : 'not-allowed',
          }}
        >
          {looking ? '...' : 'Look Up'}
        </button>
      </div>

      {/* Lookup error */}
      {lookupError && (
        <div style={{
          background: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '6px',
          padding: '14px 16px',
          marginBottom: '16px',
          fontSize: '0.85rem',
          color: '#dc2626',
          fontWeight: 600,
        }}>{lookupError}</div>
      )}

      {/* Lookup result + form */}
      {lookupResult && (
        <div style={{
          background: colors.cardBg,
          border: `1px solid ${colors.border}`,
          borderRadius: '6px',
          padding: '20px',
          marginBottom: '16px',
        }}>
          {/* Item preview */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            marginBottom: '20px',
          }}>
            <img
              src={getImageUrl()}
              alt={lookupResult.name}
              style={{
                width: '80px',
                height: '80px',
                objectFit: 'contain',
                background: '#f9fafb',
                borderRadius: '6px',
                padding: '4px',
                border: `1px solid ${colors.border}`,
              }}
              onError={e => { e.target.style.opacity = '0.15'; }}
            />
            <div>
              <div style={{ fontSize: '1rem', fontWeight: 800, color: colors.textPrimary, marginBottom: '4px' }}>
                {lookupResult.name}
              </div>
              <div style={{ fontSize: '0.82rem', color: colors.textSecondary }}>
                {partNo.trim()} · {type === 'MINIFIG' ? 'Minifig' : 'Part'}
              </div>
            </div>
          </div>

          {/* Color ID + Color Name */}
          {type !== 'MINIFIG' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '10px', marginBottom: '12px' }}>
              <div>
                <div style={{ fontSize: '0.7rem', color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>Color ID</div>
                <input
                  type="number"
                  value={colorId}
                  onChange={e => setColorId(e.target.value)}
                  placeholder="0"
                  style={{
                    width: '100%', padding: '10px 12px', background: '#f9fafb',
                    border: `1px solid ${colors.border}`, borderRadius: '6px',
                    fontFamily: "'Outfit', sans-serif", fontSize: '0.95rem', fontWeight: 700,
                    color: colors.textPrimary, outline: 'none', boxSizing: 'border-box', textAlign: 'center',
                  }}
                />
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>Color Name (for reference)</div>
                <input
                  type="text"
                  value={colorName}
                  onChange={e => setColorName(e.target.value)}
                  placeholder="e.g. Red, Dark Bluish Gray"
                  style={{
                    width: '100%', padding: '10px 12px', background: '#f9fafb',
                    border: `1px solid ${colors.border}`, borderRadius: '6px',
                    fontFamily: "'Outfit', sans-serif", fontSize: '0.9rem',
                    color: colors.textPrimary, outline: 'none', boxSizing: 'border-box',
                  }}
                />
              </div>
            </div>
          )}

          {/* Qty + Price */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
            <div>
              <div style={{ fontSize: '0.7rem', color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>Quantity</div>
              <input
                type="number"
                value={qty}
                min="1"
                onChange={e => setQty(e.target.value)}
                style={{
                  width: '100%', padding: '10px 12px', background: '#f9fafb',
                  border: `1px solid ${colors.border}`, borderRadius: '6px',
                  fontFamily: "'Outfit', sans-serif", fontSize: '1.1rem', fontWeight: 700,
                  color: colors.textPrimary, outline: 'none', boxSizing: 'border-box', textAlign: 'center',
                }}
              />
            </div>
            <div>
              <div style={{ fontSize: '0.7rem', color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>Price</div>
              <input
                type="number"
                step="0.001"
                value={price}
                onChange={e => setPrice(e.target.value)}
                placeholder="0.000"
                style={{
                  width: '100%', padding: '10px 12px', background: '#f9fafb',
                  border: `1px solid ${colors.border}`, borderRadius: '6px',
                  fontFamily: "'Outfit', sans-serif", fontSize: '1.1rem', fontWeight: 700,
                  color: colors.textPrimary, outline: 'none', boxSizing: 'border-box', textAlign: 'center',
                }}
              />
            </div>
          </div>

          {/* Condition + Bin */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' }}>
            <div>
              <div style={{ fontSize: '0.7rem', color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>Condition</div>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button
                  onClick={() => setCondition('N')}
                  style={{
                    flex: 1, padding: '10px',
                    background: condition === 'N' ? colors.navy : colors.cardBg,
                    color: condition === 'N' ? '#fff' : colors.textPrimary,
                    border: `1px solid ${condition === 'N' ? colors.navy : colors.border}`,
                    borderRadius: '6px', fontFamily: "'Outfit', sans-serif",
                    fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer',
                  }}
                >New</button>
                <button
                  onClick={() => setCondition('U')}
                  style={{
                    flex: 1, padding: '10px',
                    background: condition === 'U' ? '#d97706' : colors.cardBg,
                    color: condition === 'U' ? '#fff' : colors.textPrimary,
                    border: `1px solid ${condition === 'U' ? '#d97706' : colors.border}`,
                    borderRadius: '6px', fontFamily: "'Outfit', sans-serif",
                    fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer',
                  }}
                >Used</button>
              </div>
            </div>
            <div>
              <div style={{ fontSize: '0.7rem', color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>Bin</div>
              <input
                type="text"
                value={bin}
                onChange={e => setBin(e.target.value)}
                style={{
                  width: '100%', padding: '10px 12px', background: '#f9fafb',
                  border: `1px solid ${colors.border}`, borderRadius: '6px',
                  fontFamily: "'Outfit', sans-serif", fontSize: '0.95rem', fontWeight: 700,
                  color: colors.textPrimary, outline: 'none', boxSizing: 'border-box', textAlign: 'center',
                }}
              />
            </div>
          </div>

          {/* Push error */}
          {pushError && (
            <div style={{
              background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px',
              padding: '10px 14px', marginBottom: '12px', fontSize: '0.82rem', color: '#dc2626',
            }}>{pushError}</div>
          )}

          {/* Push button */}
          <button
            onClick={handlePush}
            disabled={pushing || !qty || parseInt(qty) <= 0}
            style={{
              width: '100%',
              padding: '14px',
              background: !pushing && qty && parseInt(qty) > 0 ? colors.greenDark : '#e2e4e8',
              color: !pushing && qty && parseInt(qty) > 0 ? '#fff' : colors.textSecondary,
              border: 'none',
              borderRadius: '6px',
              fontFamily: "'Outfit', sans-serif",
              fontWeight: 800,
              fontSize: '1rem',
              cursor: !pushing && qty && parseInt(qty) > 0 ? 'pointer' : 'not-allowed',
            }}
          >
            {pushing ? 'Pushing...' : `Push ${qty || 0}x ${partNo.trim()} to BrickLink`}
          </button>
        </div>
      )}

      {/* Empty state */}
      {!lookupResult && !lookupError && !looking && (
        <div style={{
          background: colors.cardBg,
          border: `1px solid ${colors.border}`,
          borderRadius: '6px',
          padding: '40px',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '12px', color: colors.navy }}>+</div>
          <div style={{ fontSize: '1.1rem', fontWeight: 700, color: colors.textPrimary, marginBottom: '8px' }}>
            Add a single item
          </div>
          <div style={{ fontSize: '0.85rem', color: colors.textSecondary, lineHeight: 1.6 }}>
            Type a part or minifig number above.
            <br />Set quantity, price, bin, and push straight to BrickLink.
          </div>
        </div>
      )}
    </div>
  );
}

export default QuickAdd;
